import { URLS } from '@/constants';
import { useUserStore } from '@/piniaStores/user/user.store';
import type {
  CustomerFeedback,
  ParInsight,
  ProductCategory,
  ProductInventoryInsight,
  ProductInventoryInsightsMetadata,
  SkuTier,
} from '@/services/api/models/inventoryInsights';
import { apiSlugSession, createSession } from '@/services/session';
import JSONTransform from '@/utils/json-transform';

import { NegotiationMessageRequest, NegotiationMessageResponse } from './models/negotiationMessage';

export type TypeOfDate = 'today' | 'yesterday' | 'last_week' | 'last_month' | 'end_of_last_month';

export interface InventoryInsightParams {
  brandIds?: string[] | number[];
  brandNames?: string[];
  productCategory?: ProductCategory;
  search?: string;
  isBottom20Pct?: boolean;
  order?: 'asc' | 'desc';
  orderBy?: string;
  periodEnding?: string;
  skuTiers?: SkuTier[];
}

export type ParInsightParams = Pick<InventoryInsightParams, 'brandIds'>;

export interface BrandFilter {
  name: string;
}

export interface InventoryInsightsResponse {
  buyerId: number;
  insights: Array<ProductInventoryInsight>;
}

export interface ParInsightsResponse {
  buyerId: number;
  insights: ParInsight[];
}

export interface InventoryInsightsMetadata {
  lastIngestionAt?: string;
  brands?: string[];
}

export async function getProductInsights(params: InventoryInsightParams): Promise<InventoryInsightsResponse> {
  const paramsFormatted = new JSONTransform({
    ...params,
    errorFormat: 'jsonapi',
  }).toSnakeCaseKeys();

  const productInsightsSession = createSession(
    () => {
      const userStore = useUserStore();

      return URLS.INTERNAL + userStore.userActiveCompany?.slug;
    },
    {
      arrayFormat: 'repeat',
    },
  );

  const response = await productInsightsSession.get<InventoryInsightsResponse>(URLS.INVENTORY_INSIGHTS.LEADERBOARD, {
    params: { ...paramsFormatted },
  });

  return response.data;
}

export async function getProductInsightsMetadata() {
  const response = await apiSlugSession.get<ProductInventoryInsightsMetadata>(URLS.INVENTORY_INSIGHTS.METADATA);
  return response.data;
}

export async function getParInsights(params: ParInsightParams) {
  const response = await apiSlugSession.get<ParInsightsResponse>(URLS.INVENTORY_INSIGHTS.PAR, {
    params: { brandIds: params.brandIds?.join(',') },
  });

  return response.data;
}

export async function sendCustomerFeedback(params: CustomerFeedback) {
  const paramsFormatted = {
    ...params,
    issue_type: params.issueType,
  };

  await apiSlugSession.post(URLS.INVENTORY_INSIGHTS.CUSTOMER_FEEDBACK, paramsFormatted);
}

export async function sendNegotiationMessage(params: NegotiationMessageRequest): Promise<NegotiationMessageResponse> {
  const response = await apiSlugSession.post(URLS.INVENTORY_INSIGHTS.NEGOTIATE, params);
  return response.data;
}
